import React from 'react';
import { cn } from '../../utils/cn';
import { CheckCircle, X } from 'lucide-react';
import { Button } from './Button';

export interface FormSuccessMessageProps {
  message: string | null;
  onDismiss?: () => void;
  className?: string;
  variant?: 'inline' | 'banner';
  showIcon?: boolean;
  autoHide?: boolean;
  autoHideDelay?: number;
}

/**
 * Reusable success message component for forms
 * Provides consistent styling and accessibility features
 */
export const FormSuccessMessage: React.FC<FormSuccessMessageProps> = ({
  message,
  onDismiss,
  className,
  variant = 'inline',
  showIcon = true,
  autoHide = false,
  autoHideDelay = 5000
}) => {
  // Auto-hide functionality
  React.useEffect(() => {
    if (autoHide && message && onDismiss) {
      const timer = setTimeout(() => {
        onDismiss();
      }, autoHideDelay);

      return () => clearTimeout(timer);
    }
  }, [autoHide, message, onDismiss, autoHideDelay]);

  if (!message) return null;

  const baseClasses = 'flex items-center gap-2 text-sm text-green-600 dark:text-green-400';
  
  const variantClasses = {
    inline: 'p-2',
    banner: 'p-3 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-md'
  };

  return (
    <div 
      className={cn(baseClasses, variantClasses[variant], className)}
      role="alert"
      aria-live="polite"
    >
      {showIcon && (
        <CheckCircle className="w-4 h-4 flex-shrink-0" />
      )}
      
      <span className="flex-1">
        {message}
      </span>

      {onDismiss && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onDismiss}
          className="text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 h-auto p-1"
          aria-label="Dismiss success message"
        >
          <X className="w-3 h-3" />
        </Button>
      )}
    </div>
  );
};